import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Link, useNavigate, useParams, useBlocker } from 'react-router-dom';
import api from '../../API';
import { useAuth } from '../../providers/AuthContext';
import ProjectsHubBackLink from './hub/ProjectsHubBackLink';
import ProjectEditForm from './ProjectEditForm';
import SlideSetDetail from './SlideSetDetail';
import {
    getRoiConfig,
    hasRoiTypesConfigured,
} from './uis/BoundingBoxUI/roiProjectConfig';
import { isProjectSettingsDirty } from './projectSettingsDraft';
import {
    getAnnotationUiConfigPublishWarnings,
    getAnnotationUiConfigPublishBlocks,
    getUISupportsProjectRoi,
} from './uiRegistry';
import {
    PROJECT_LIFECYCLE,
    normalizeProjectLifecycleStatus,
} from './projectLifecycle';
import {
    checkRoiCoverage,
    fetchProjectItemIds,
} from './projectRoiCoverage';
import { prepareAnnotationUIForProjectSave } from './uis/SlideLevelClassificationUI/slideLevelClassificationModel';
import { getSurveyPublishBlockingReasons } from './questions/surveyModel';
import { IconPencil } from './hub/HubRowActionIcons';
import RenameResourceModal from './hub/RenameResourceModal';
import styles from './ProjectDetail.module.css';
import projectEditStyles from './ProjectEditForm.module.css';

function userIsProjectAdminFromDoc(folderDoc, user) {
    if (!folderDoc || !user) return false;
    if (user.admin) return true;
    const parts = folderDoc.girderProjects?.participants ?? [];
    return parts.some(
        (p) => String(p.userId) === String(user._id) && (p.roles ?? []).includes('admin')
    );
}

// Girder AccessType values (numeric)
const ACCESS_WRITE = 1;

function ProjectDetail() {
    const { folderId } = useParams();
    const navigate = useNavigate();
    const { user: authUser } = useAuth();

    const [doc, setDoc] = useState(null);
    const [folderType, setFolderType] = useState(null);
    const [lifecycleStatus, setLifecycleStatus] = useState(PROJECT_LIFECYCLE.DRAFT);
    const [participants, setParticipants] = useState([]);
    const [annotationUI, setAnnotationUI] = useState({ id: '', config: {} });
    const [notesConfig, setNotesConfig] = useState({ enabled: false });
    const [surveysConfig, setSurveysConfig] = useState([]);
    const [ancestors, setAncestors] = useState([]);
    const [firstItemId, setFirstItemId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const successTimerRef = useRef(null);

    const [publishRoiChecking, setPublishRoiChecking] = useState(false);
    const [roiCoverageModal, setRoiCoverageModal] = useState(null);
    const [roiCoverageSuccessCount, setRoiCoverageSuccessCount] = useState(null);
    const [publishRoiCheckError, setPublishRoiCheckError] = useState(null);
    const roiPublishCheckGenRef = useRef(0);
    const roiModalRef = useRef(null);

    const [itemCount, setItemCount] = useState(0);
    const [publishPutPending, setPublishPutPending] = useState(false);
    const [publishPutError, setPublishPutError] = useState(null);
    const [statusConfirm, setStatusConfirm] = useState(null);
    const statusConfirmRef = useRef(null);
    const [deleteConfirmName, setDeleteConfirmName] = useState('');
    const [projectRenameOpen, setProjectRenameOpen] = useState(false);

    const hasUnsavedSettings = useMemo(
        () => isProjectSettingsDirty(
            annotationUI,
            notesConfig,
            surveysConfig,
            doc?.girderProjects,
        ),
        [annotationUI, notesConfig, surveysConfig, doc?.girderProjects]
    );

    const settingsLocked = lifecycleStatus !== PROJECT_LIFECYCLE.DRAFT;

    const hasUnsavedSettingsRef = useRef(hasUnsavedSettings);
    hasUnsavedSettingsRef.current = hasUnsavedSettings;

    useEffect(() => {
        if (!hasUnsavedSettings) return undefined;
        const onBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [hasUnsavedSettings]);

    const blocker = useBlocker(hasUnsavedSettings);

    useEffect(() => {
        if (blocker.state !== 'blocked') return;
        const ok = window.confirm(
            'Unsaved annotation UI settings will be lost. Leave this page?'
        );
        if (ok) blocker.proceed();
        else blocker.reset();
    }, [blocker]);

    useEffect(() => {
        if (roiCoverageSuccessCount == null) return undefined;
        const t = setTimeout(() => setRoiCoverageSuccessCount(null), 4500);
        return () => clearTimeout(t);
    }, [roiCoverageSuccessCount]);

    useEffect(() => {
        if (!roiCoverageModal) return undefined;
        const node = roiModalRef.current;
        const focusables = node?.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
        );
        const buttons = focusables?.length
            ? [...focusables]
            : [];
        const reviewBtn = buttons.find((b) => b.textContent?.includes('Review ROIs'));
        (reviewBtn || buttons[0])?.focus();

        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setRoiCoverageModal(null);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [roiCoverageModal]);

    useEffect(() => {
        if (!statusConfirm) return undefined;
        const node = statusConfirmRef.current;
        const autofocusTarget = node?.querySelector('[data-autofocus="true"]');
        const fallbackButton = node?.querySelector('button');
        (autofocusTarget || fallbackButton)?.focus();
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setStatusConfirm(null);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [statusConfirm]);

    useEffect(() => {
        setLoading(true);
        setError(null);
        setAncestors([]);
        setFolderType(null);

        const projectPromise = api.get(`projects/${folderId}`, {});
        const rootpathPromise = api.get(`folder/${folderId}/rootpath`, {});
        const itemsPromise = api.get(`projects/${folderId}/items`, {});

        Promise.all([projectPromise, rootpathPromise, itemsPromise])
            .then(([projectRes, rootpathRes, itemsRes]) => {
                if (projectRes.status === 200) {
                    const folder = projectRes.data;
                    const gpType = folder?.girderProjects?.type;
                    if (gpType === 'slideset') {
                        setFolderType('slideset');
                        setLoading(false);
                        return;
                    }
                    setFolderType(gpType || 'project');
                    if (typeof folder.myAccess === 'number' && folder.myAccess < ACCESS_WRITE) {
                        navigate(`/projects/${folderId}/participate`, { replace: true });
                        return;
                    }
                    setDoc(folder);
                    const gp = folder.girderProjects ?? {};
                    setLifecycleStatus(normalizeProjectLifecycleStatus(gp.lifecycleStatus));
                    setParticipants(gp.participants ?? []);
                    setAnnotationUI(gp.annotationUI ?? { id: '', config: {} });
                    setNotesConfig(gp.notes ?? { enabled: false });
                    setSurveysConfig(Array.isArray(gp.surveys) ? gp.surveys : []);
                } else {
                    setError(projectRes.data?.message || 'Failed to load project.');
                }

                if (rootpathRes.status === 200 && Array.isArray(rootpathRes.data)) {
                    const projectAncestors = rootpathRes.data
                        .filter((entry) => entry.type === 'folder' && entry.object?.girderProjects)
                        .map((entry) => entry.object)
                        .filter((f) => String(f._id) !== String(folderId));
                    setAncestors(projectAncestors);
                }

                if (itemsRes.status === 200 && itemsRes.data?.items?.length > 0) {
                    setFirstItemId(String(itemsRes.data.items[0]._id));
                } else {
                    setFirstItemId(null);
                }
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [folderId, navigate]);

    const applyParticipantRole = useCallback(
        async ({ op, userId, role }) => {
            if (!doc) {
                return { status: 400, data: { message: 'Project not loaded.' } };
            }
            const r = await api.post(`projects/${folderId}/participants/role`, {
                json: { op, userId, role },
            });
            if (r.status === 200) {
                setDoc(r.data);
                setParticipants(r.data.girderProjects?.participants ?? []);
                if (!hasUnsavedSettingsRef.current) {
                    const nextGp = r.data.girderProjects ?? {};
                    setLifecycleStatus(normalizeProjectLifecycleStatus(nextGp.lifecycleStatus));
                    setAnnotationUI(nextGp.annotationUI ?? { id: '', config: {} });
                setNotesConfig(nextGp.notes ?? { enabled: false });
                    setSurveysConfig(Array.isArray(nextGp.surveys) ? nextGp.surveys : []);
                }
            }
            return r;
        },
        [doc, folderId]
    );

    function handleSave() {
        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        clearTimeout(successTimerRef.current);
        const payload = {
            ...doc?.girderProjects,
            lifecycleStatus,
            annotationUI: prepareAnnotationUIForProjectSave(annotationUI),
            notes: notesConfig,
            surveys: surveysConfig,
        };
        api.put(`projects/${folderId}`, { json: payload })
            .then((r) => {
                if (r.status === 200) {
                    setDoc(r.data);
                    const gp = r.data.girderProjects ?? {};
                    setLifecycleStatus(normalizeProjectLifecycleStatus(gp.lifecycleStatus));
                    setParticipants(gp.participants ?? []);
                    setAnnotationUI(gp.annotationUI ?? { id: '', config: {} });
                    setNotesConfig(gp.notes ?? { enabled: false });
                    setSurveysConfig(Array.isArray(gp.surveys) ? gp.surveys : []);
                    setSaveSuccess(true);
                    successTimerRef.current = setTimeout(() => setSaveSuccess(false), 3000);
                } else {
                    setSaveError(r.data?.message || 'Save failed.');
                }
            })
            .catch((e) => setSaveError(e.message))
            .finally(() => setSaving(false));
    }

    function handleDiscardSettings() {
        const gp = doc?.girderProjects ?? {};
        roiPublishCheckGenRef.current += 1;
        setPublishRoiChecking(false);
        setRoiCoverageModal(null);
        setRoiCoverageSuccessCount(null);
        setPublishRoiCheckError(null);
        setLifecycleStatus(normalizeProjectLifecycleStatus(gp.lifecycleStatus));
        setAnnotationUI(gp.annotationUI ?? { id: '', config: {} });
        setNotesConfig(gp.notes ?? { enabled: false });
                    setSurveysConfig(Array.isArray(gp.surveys) ? gp.surveys : []);
        setSaveError(null);
        setPublishPutError(null);
    }

    const handleItemsChanged = useCallback((items) => {
        const list = items ?? [];
        setFirstItemId(list.length > 0 ? String(list[0]._id) : null);
        setItemCount(list.length);
    }, []);

    const publishWarnings = useMemo(() => {
        const w = [];
        if (!annotationUI?.id) w.push('No annotation UI selected.');
        if (itemCount === 0) w.push('No items in this project.');
        const cfg = annotationUI?.config ?? {};
        const roi = getRoiConfig(cfg);
        if (roi.enabled && hasRoiTypesConfigured(cfg) && !roi.adminRoiDrawingComplete) {
            w.push('ROI mode on — confirm ROIs in Regions of interest below, then Save.');
        }
        w.push(...getAnnotationUiConfigPublishWarnings(annotationUI));
        return w;
    }, [annotationUI, itemCount]);

    const publishBlocks = useMemo(
        () => [
            ...getAnnotationUiConfigPublishBlocks(annotationUI),
            ...getSurveyPublishBlockingReasons(surveysConfig),
        ],
        [annotationUI, surveysConfig],
    );

    const persistLifecycleStatus = useCallback(
        async (nextStatus) => {
            if (!doc) return { ok: false };
            setPublishPutPending(true);
            setPublishPutError(null);
            try {
                const payload = {
                    ...doc.girderProjects,
                    lifecycleStatus: nextStatus,
                    annotationUI: prepareAnnotationUIForProjectSave(annotationUI),
                    notes: notesConfig,
                    surveys: surveysConfig,
                };
                const r = await api.put(`projects/${folderId}`, { json: payload });
                if (r.status === 200) {
                    setDoc(r.data);
                    const gp = r.data.girderProjects ?? {};
                    setLifecycleStatus(normalizeProjectLifecycleStatus(gp.lifecycleStatus));
                    setParticipants(gp.participants ?? []);
                    setAnnotationUI(gp.annotationUI ?? { id: '', config: {} });
                    setNotesConfig(gp.notes ?? { enabled: false });
                    setSurveysConfig(Array.isArray(gp.surveys) ? gp.surveys : []);
                    setRoiCoverageModal(null);
                    setStatusConfirm(null);
                    setPublishRoiCheckError(null);
                    return { ok: true };
                }
                const msg = typeof r.data === 'object' && r.data?.message
                    ? r.data.message
                    : 'Update failed.';
                setPublishPutError(msg);
                return { ok: false };
            } catch (e) {
                setPublishPutError(e.message || 'Update failed.');
                return { ok: false };
            } finally {
                setPublishPutPending(false);
            }
        },
        [doc, folderId, annotationUI, notesConfig, surveysConfig]
    );

    const runPublishRoiThenPersist = useCallback(async () => {
        setStatusConfirm(null);
        const cfg = annotationUI?.config ?? {};
        const roi = getRoiConfig(cfg);
        const needsRoiCheck = roi.enabled && hasRoiTypesConfigured(cfg);

        if (!needsRoiCheck) {
            await persistLifecycleStatus(PROJECT_LIFECYCLE.PUBLISHED);
            return;
        }

        const gen = roiPublishCheckGenRef.current + 1;
        roiPublishCheckGenRef.current = gen;
        setPublishRoiCheckError(null);
        setRoiCoverageSuccessCount(null);
        setPublishRoiChecking(true);

        try {
            const { ok, itemIds, error } = await fetchProjectItemIds(api, folderId);
            if (roiPublishCheckGenRef.current !== gen) return;
            if (!ok) {
                setPublishRoiCheckError(error);
                return;
            }
            if (itemIds.length === 0) {
                await persistLifecycleStatus(PROJECT_LIFECYCLE.PUBLISHED);
                return;
            }
            const { missingIds } = await checkRoiCoverage(api, folderId, itemIds, {
                concurrency: 6,
                isCancelled: () => roiPublishCheckGenRef.current !== gen,
            });
            if (roiPublishCheckGenRef.current !== gen) return;
            if (missingIds.length === 0) {
                setRoiCoverageSuccessCount(itemIds.length);
                await persistLifecycleStatus(PROJECT_LIFECYCLE.PUBLISHED);
            } else {
                setRoiCoverageModal({
                    missingCount: missingIds.length,
                    totalSlides: itemIds.length,
                    firstItemId: missingIds[0],
                });
            }
        } catch (err) {
            if (roiPublishCheckGenRef.current !== gen) return;
            setPublishRoiCheckError(err?.message || 'Could not verify ROI coverage.');
        } finally {
            if (roiPublishCheckGenRef.current === gen) {
                setPublishRoiChecking(false);
            }
        }
    }, [annotationUI, folderId, persistLifecycleStatus]);

    const executeDeleteProject = useCallback(async () => {
        setPublishPutPending(true);
        setPublishPutError(null);
        try {
            const r = await api.delete(`projects/${folderId}`, {});
            if (r.status === 200) {
                navigate('/projects/manage', { replace: true });
            } else {
                setPublishPutError(r.data?.message || 'Delete failed.');
            }
        } catch (e) {
            setPublishPutError(e.response?.data?.message || e.message || 'Delete failed.');
        } finally {
            setPublishPutPending(false);
        }
    }, [folderId, navigate]);

    const uiCfg = annotationUI?.config ?? {};
    const showRoiEditorLink =
        userIsProjectAdminFromDoc(doc, authUser)
        && getUISupportsProjectRoi(annotationUI?.id)
        && hasRoiTypesConfigured(uiCfg)
        && getRoiConfig(uiCfg).enabled;

    const handleRoiModalKeyDown = useCallback((e) => {
        if (e.key !== 'Tab' || !roiModalRef.current) return;
        const sel = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])';
        const nodes = [...roiModalRef.current.querySelectorAll(sel)];
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }, []);

    if (loading) {
        return <div className={styles.loading}>Loading project…</div>;
    }

    if (folderType === 'slideset') {
        return <SlideSetDetail />;
    }

    if (error) {
        return (
            <div>
                <div className={styles.errorMessage}>{error}</div>
                <ProjectsHubBackLink className={styles.backLink} variant="long" />
            </div>
        );
    }

    if (!doc) {
        return (
            <div>
                <p>Project not found.</p>
                <ProjectsHubBackLink className={styles.backLink} variant="long" />
            </div>
        );
    }

    return (
        <div className={hasUnsavedSettings ? styles.pageWithStickyDraft : undefined}>
            {hasUnsavedSettings && (
                <div className={styles.stickyDraftBar} role="region" aria-label="Unsaved project settings">
                    <div className={styles.stickyDraftBarInner}>
                        <span className={styles.stickyDraftMessage}>Unsaved UI settings</span>
                        <div className={styles.stickyDraftActions}>
                            <button
                                type="button"
                                className={styles.buttonSecondary}
                                onClick={handleDiscardSettings}
                                disabled={saving}
                            >
                                Discard changes
                            </button>
                            <button
                                type="button"
                                className={styles.buttonPrimary}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <nav className={styles.breadcrumb} aria-label="Project navigation">
                        <ProjectsHubBackLink className={styles.breadcrumbLink} variant="root" />
                        {ancestors.map((f) => (
                            <span key={f._id} style={{ display: 'contents' }}>
                                <span className={styles.breadcrumbSeparator}>/</span>
                                <Link
                                    to={`/projects/manage/${f._id}`}
                                    className={styles.breadcrumbLink}
                                >
                                    {f.name}
                                </Link>
                            </span>
                        ))}
                        <span className={styles.breadcrumbSeparator}>/</span>
                        <span className={styles.breadcrumbCurrent}>{doc.name}</span>
                    </nav>
                    <div className={styles.titleRow}>
                        <h3 className={styles.pageTitle}>{doc.name}</h3>
                        {typeof doc.myAccess === 'number' && doc.myAccess >= ACCESS_WRITE ? (
                            <button
                                type="button"
                                className={styles.titleRenameBtn}
                                title="Rename project"
                                aria-label={`Rename project ${doc.name}`}
                                onClick={() => setProjectRenameOpen(true)}
                            >
                                <IconPencil />
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>

            {saveError && (
                <div className={styles.errorMessage}>{saveError}</div>
            )}
            {saveSuccess && (
                <div className={styles.successMessage}>Project saved.</div>
            )}

            <ProjectEditForm
                lifecycleStatus={lifecycleStatus}
                settingsLocked={settingsLocked}
                onRequestPublish={() => setStatusConfirm('publish')}
                onRequestReturnToDraft={() => setStatusConfirm('toDraft')}
                onRequestClose={() => setStatusConfirm('close')}
                onRequestArchive={() => setStatusConfirm('archive')}
                onRequestReopen={() => setStatusConfirm('reopen')}
                onRequestUnarchive={() => setStatusConfirm('unarchive')}
                onRequestDeleteProject={() => {
                    setDeleteConfirmName('');
                    setPublishPutError(null);
                    setStatusConfirm('delete');
                }}
                participants={participants}
                onApplyParticipantRole={applyParticipantRole}
                annotationUI={annotationUI}
                setAnnotationUI={setAnnotationUI}
                notesConfig={notesConfig}
                setNotesConfig={setNotesConfig}
                surveysConfig={surveysConfig}
                setSurveysConfig={setSurveysConfig}
                folderId={folderId}
                onItemsChanged={handleItemsChanged}
                creatorId={doc?.creatorId}
                hasUnsavedSettings={hasUnsavedSettings}
                firstItemId={firstItemId}
                showRoiEditorLink={showRoiEditorLink}
                publishRoiChecking={publishRoiChecking}
                publishPutPending={publishPutPending}
                publishPutError={publishPutError}
                publishWarnings={publishWarnings}
                publishBlocks={publishBlocks}
                roiCoverageSuccessCount={roiCoverageSuccessCount}
                publishRoiCheckError={publishRoiCheckError}
            />

            {statusConfirm === 'publish' && (
                <div
                    className={projectEditStyles.confirmOverlay}
                    role="presentation"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setStatusConfirm(null);
                    }}
                >
                    <div
                        ref={statusConfirmRef}
                        className={`${projectEditStyles.confirmDialog} ${projectEditStyles.confirmDialogWide}`}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="status-confirm-publish-title"
                    >
                        <h2
                            id="status-confirm-publish-title"
                            className={projectEditStyles.confirmDialogTitle}
                        >
                            Publish this project?
                        </h2>
                        <p className={projectEditStyles.confirmMsg}>
                            Assigned participants will be able to open this project.
                        </p>
                        {publishWarnings.length > 0 && (
                            <div
                                className={projectEditStyles.publishWarningsBox}
                                role="region"
                                aria-labelledby="publish-modal-warnings-heading"
                            >
                                <div className={projectEditStyles.publishWarningsHeader}>
                                    <span className={projectEditStyles.publishWarningsIcon} aria-hidden>
                                        !
                                    </span>
                                    <strong
                                        id="publish-modal-warnings-heading"
                                        className={projectEditStyles.publishWarningsHeading}
                                    >
                                        Before publishing
                                    </strong>
                                </div>
                                <ul className={projectEditStyles.publishWarningsList}>
                                    {publishWarnings.map((w) => (
                                        <li key={w} className={projectEditStyles.publishWarningsItem}>
                                            <span
                                                className={projectEditStyles.publishWarningsItemIcon}
                                                aria-hidden
                                            >
                                                !
                                            </span>
                                            <span>{w}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className={projectEditStyles.confirmActions}>
                            <button
                                type="button"
                                className={projectEditStyles.buttonSecondary}
                                onClick={() => setStatusConfirm(null)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={projectEditStyles.buttonPrimary}
                                onClick={() => { void runPublishRoiThenPersist(); }}
                                disabled={publishPutPending}
                            >
                                Publish
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {statusConfirm === 'close' && (
                <div
                    className={projectEditStyles.confirmOverlay}
                    role="presentation"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setStatusConfirm(null);
                    }}
                >
                    <div
                        ref={statusConfirmRef}
                        className={projectEditStyles.confirmDialog}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="status-confirm-close-title"
                        onKeyDown={handleRoiModalKeyDown}
                    >
                        <h2
                            id="status-confirm-close-title"
                            className={projectEditStyles.confirmDialogTitle}
                        >
                            Close project?
                        </h2>
                        <p className={projectEditStyles.confirmMsg}>
                            Participants will no longer be able to annotate while the project is closed.
                        </p>
                        <div className={projectEditStyles.confirmActions}>
                            <button
                                type="button"
                                className={projectEditStyles.buttonSecondary}
                                onClick={() => setStatusConfirm(null)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={projectEditStyles.buttonPrimary}
                                onClick={() => { void persistLifecycleStatus(PROJECT_LIFECYCLE.CLOSED); }}
                                disabled={publishPutPending}
                            >
                                Close project
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {statusConfirm === 'toDraft' && (
                <div
                    className={projectEditStyles.confirmOverlay}
                    role="presentation"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setStatusConfirm(null);
                    }}
                >
                    <div
                        ref={statusConfirmRef}
                        className={projectEditStyles.confirmDialog}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="status-confirm-todraft-title"
                        onKeyDown={handleRoiModalKeyDown}
                    >
                        <h2
                            id="status-confirm-todraft-title"
                            className={projectEditStyles.confirmDialogTitle}
                        >
                            Return project to draft?
                        </h2>
                        <p className={projectEditStyles.confirmMsg}>
                            Returning to draft hides this project from participants and unlocks project settings for edits.
                        </p>
                        <div className={projectEditStyles.confirmActions}>
                            <button
                                type="button"
                                className={projectEditStyles.buttonSecondary}
                                onClick={() => setStatusConfirm(null)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={projectEditStyles.buttonPrimary}
                                onClick={() => { void persistLifecycleStatus(PROJECT_LIFECYCLE.DRAFT); }}
                                disabled={publishPutPending}
                            >
                                Return to draft
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {statusConfirm === 'archive' && (
                <div
                    className={projectEditStyles.confirmOverlay}
                    role="presentation"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setStatusConfirm(null);
                    }}
                >
                    <div
                        ref={statusConfirmRef}
                        className={projectEditStyles.confirmDialog}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="status-confirm-archive-title"
                        onKeyDown={handleRoiModalKeyDown}
                    >
                        <h2
                            id="status-confirm-archive-title"
                            className={projectEditStyles.confirmDialogTitle}
                        >
                            Archive project?
                        </h2>
                        <p className={projectEditStyles.confirmMsg}>
                            Archived projects are hidden from participant My Projects and annotation access remains disabled.
                        </p>
                        <div className={projectEditStyles.confirmActions}>
                            <button
                                type="button"
                                className={projectEditStyles.buttonSecondary}
                                onClick={() => setStatusConfirm(null)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={projectEditStyles.buttonPrimary}
                                onClick={() => { void persistLifecycleStatus(PROJECT_LIFECYCLE.ARCHIVED); }}
                                disabled={publishPutPending}
                            >
                                Archive
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {statusConfirm === 'reopen' && (
                <div
                    className={projectEditStyles.confirmOverlay}
                    role="presentation"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setStatusConfirm(null);
                    }}
                >
                    <div
                        ref={statusConfirmRef}
                        className={projectEditStyles.confirmDialog}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="status-confirm-reopen-title"
                        onKeyDown={handleRoiModalKeyDown}
                    >
                        <h2
                            id="status-confirm-reopen-title"
                            className={projectEditStyles.confirmDialogTitle}
                        >
                            Reopen project?
                        </h2>
                        <p className={projectEditStyles.confirmMsg}>
                            Reopening moves this project back to published so participants can annotate again.
                        </p>
                        <div className={projectEditStyles.confirmActions}>
                            <button
                                type="button"
                                className={projectEditStyles.buttonSecondary}
                                onClick={() => setStatusConfirm(null)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={projectEditStyles.buttonPrimary}
                                onClick={() => { void persistLifecycleStatus(PROJECT_LIFECYCLE.PUBLISHED); }}
                                disabled={publishPutPending}
                            >
                                Reopen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {statusConfirm === 'unarchive' && (
                <div
                    className={projectEditStyles.confirmOverlay}
                    role="presentation"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setStatusConfirm(null);
                    }}
                >
                    <div
                        ref={statusConfirmRef}
                        className={projectEditStyles.confirmDialog}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="status-confirm-unarchive-title"
                        onKeyDown={handleRoiModalKeyDown}
                    >
                        <h2
                            id="status-confirm-unarchive-title"
                            className={projectEditStyles.confirmDialogTitle}
                        >
                            Unarchive project?
                        </h2>
                        <p className={projectEditStyles.confirmMsg}>
                            Unarchiving moves this project to closed. Participants can see it again but cannot annotate until reopened.
                        </p>
                        <div className={projectEditStyles.confirmActions}>
                            <button
                                type="button"
                                className={projectEditStyles.buttonSecondary}
                                onClick={() => setStatusConfirm(null)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={projectEditStyles.buttonPrimary}
                                onClick={() => { void persistLifecycleStatus(PROJECT_LIFECYCLE.CLOSED); }}
                                disabled={publishPutPending}
                            >
                                Unarchive
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {statusConfirm === 'delete' && (
                <div
                    className={projectEditStyles.confirmOverlay}
                    role="presentation"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setDeleteConfirmName('');
                            setStatusConfirm(null);
                        }
                    }}
                >
                    <div
                        ref={statusConfirmRef}
                        className={projectEditStyles.confirmDialog}
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby="status-confirm-delete-title"
                        aria-describedby="status-confirm-delete-msg"
                        onKeyDown={handleRoiModalKeyDown}
                    >
                        <h2
                            id="status-confirm-delete-title"
                            className={projectEditStyles.confirmDialogTitle}
                        >
                            Delete project?
                        </h2>
                        <p id="status-confirm-delete-msg" className={projectEditStyles.confirmMsg}>
                            This permanently deletes the project and all nested folders/items.
                            Type
                            {' '}
                            <strong>{doc?.name ?? ''}</strong>
                            {' '}
                            to confirm.
                        </p>
                        <div className={projectEditStyles.confirmInputRow}>
                            <label className={projectEditStyles.confirmInputLabel} htmlFor="delete-project-confirm-name">
                                Project name
                            </label>
                            <input
                                id="delete-project-confirm-name"
                                data-autofocus="true"
                                className={projectEditStyles.confirmInput}
                                type="text"
                                autoComplete="off"
                                value={deleteConfirmName}
                                onChange={(e) => setDeleteConfirmName(e.target.value)}
                            />
                        </div>
                        <div className={projectEditStyles.confirmActions}>
                            <button
                                type="button"
                                className={projectEditStyles.buttonSecondary}
                                onClick={() => {
                                    setDeleteConfirmName('');
                                    setStatusConfirm(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={projectEditStyles.buttonDanger}
                                onClick={() => { void executeDeleteProject(); }}
                                disabled={publishPutPending || deleteConfirmName.trim() !== (doc?.name ?? '').trim()}
                            >
                                Delete project
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {roiCoverageModal && (
                <div
                    className={projectEditStyles.confirmOverlay}
                    role="presentation"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setRoiCoverageModal(null);
                    }}
                >
                    <div
                        ref={roiModalRef}
                        className={`${projectEditStyles.confirmDialog} ${projectEditStyles.confirmDialogWide}`}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="roi-publish-coverage-title"
                        onKeyDown={handleRoiModalKeyDown}
                    >
                        <h2
                            id="roi-publish-coverage-title"
                            className={projectEditStyles.confirmDialogTitle}
                        >
                            Some slides are missing ROI data
                        </h2>
                        <p className={projectEditStyles.confirmMsg}>
                            {roiCoverageModal.missingCount}
                            {' '}
                            of
                            {' '}
                            {roiCoverageModal.totalSlides}
                            {' '}
                            slide
                            {roiCoverageModal.totalSlides === 1 ? '' : 's'}
                            {' '}
                            lack project ROIs. Annotators may miss guidance until ROIs exist.
                        </p>
                        <div className={projectEditStyles.confirmActions}>
                            <button
                                type="button"
                                className={projectEditStyles.buttonSecondary}
                                onClick={() => setRoiCoverageModal(null)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={projectEditStyles.buttonSecondary}
                                onClick={() => {
                                    setRoiCoverageModal(null);
                                    void persistLifecycleStatus(PROJECT_LIFECYCLE.PUBLISHED);
                                }}
                            >
                                Publish anyway
                            </button>
                            <button
                                type="button"
                                className={projectEditStyles.buttonPrimary}
                                onClick={() => {
                                    const id = roiCoverageModal.firstItemId;
                                    setRoiCoverageModal(null);
                                    if (id) {
                                        navigate(`/projects/${folderId}/roi-edit/${id}`);
                                    }
                                }}
                            >
                                Review ROIs
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <RenameResourceModal
                open={projectRenameOpen}
                folderId={folderId}
                initialName={doc?.name ?? ''}
                onClose={() => setProjectRenameOpen(false)}
                onSuccess={(data) => {
                    setDoc((d) => (d ? { ...d, ...data } : d));
                }}
            />
        </div>
    );
}

export default ProjectDetail;
